import { useEffect, useState } from "react";
import { Crown, Sparkles, ArrowRight, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const PLAN_STYLES = {
    silver: { label: "Silver", glow: "from-slate-100 to-white", chip: "bg-slate-200 text-slate-700" },
    gold: { label: "Gold", glow: "from-[#FFFBEF] to-white", chip: "bg-gold-500/15 text-gold-700 border border-gold-500/40" },
    platinum: { label: "Platinum", glow: "from-[#FFFBEF] via-white to-[#FFE9C8]", chip: "bg-gradient-to-r from-gold-500 to-amber-400 text-on-image text-white" },
};

export function SubscriptionCard() {
    const [data, setData] = useState(null);

    const reload = () => api.get("/subscriptions/my-plan").then((r) => setData(r.data));
    useEffect(() => { reload(); }, []);

    const cancel = async () => {
        if (!window.confirm("Cancel auto-renewal? You'll keep access till the end of the current period.")) return;
        try {
            await api.post("/subscriptions/cancel", {});
            toast.success("Subscription cancelled");
            reload();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    if (!data) return null;
    const plan = data.active_plan;
    const expiresAt = data.plan_expires_at ? new Date(data.plan_expires_at) : null;
    const expired = expiresAt && expiresAt.getTime() < Date.now();
    const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 86400000)) : 0;
    const isActive = plan && !expired && !!PLAN_STYLES[plan];
    const styles = plan ? PLAN_STYLES[plan] : null;

    if (!isActive) {
        return (
            <div className="text-on-image rounded-2xl bg-gradient-to-br from-[#002657] to-[#1a3a6e] text-white p-6 shadow-md" data-testid="subscription-card-upgrade">
                <div className="flex items-center justify-between">
                    <Sparkles className="w-5 h-5 text-gold-300" />
                    <span className="text-[10px] uppercase tracking-widest text-gold-300/90 font-semibold">{plan && expired ? "Expired" : "No Active Plan"}</span>
                </div>
                <div className="mt-3 font-heading text-2xl font-bold leading-tight text-white">Unlock Silver, Gold or Platinum</div>
                <p className="text-sm text-white/80 mt-2 leading-relaxed">Get featured listings, priority support, premium badges & more — starting at just ₹1,000 for 30 days.</p>
                <Link
                    to="/pricing"
                    data-testid="subscription-upgrade-cta"
                    className="text-on-image mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-gold-500 text-white font-semibold text-sm hover:bg-gold-600"
                >
                    View Plans <ArrowRight className="w-4 h-4" />
                </Link>
            </div>
        );
    }

    return (
        <div className={`rounded-2xl border-2 border-gold-500/40 bg-gradient-to-br ${styles.glow} p-6 shadow-sm`} data-testid="subscription-card-active">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl bg-gold-500 flex items-center justify-center shadow-md text-on-image">
                        <Crown className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <div className="label-overline">Active Plan</div>
                        <div className="font-heading text-2xl font-bold text-navy-500">{styles.label}</div>
                    </div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-bold ${styles.chip}`}>
                    {data.subscription_status === "cancelled" ? "Cancelled" : "Active"}
                </span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                    <div className="text-xs text-navy-500/60 uppercase tracking-wider font-semibold">Valid till</div>
                    <div className="text-navy-500 font-bold mt-0.5">{expiresAt?.toLocaleDateString()}</div>
                </div>
                <div>
                    <div className="text-xs text-navy-500/60 uppercase tracking-wider font-semibold">Days remaining</div>
                    <div className="text-navy-500 font-bold mt-0.5">{daysLeft}</div>
                </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-5">
                <Link to="/pricing" className="text-on-image px-4 py-2 rounded-md bg-navy-500 text-white text-sm font-semibold hover:bg-[#1a3a6e]" data-testid="subscription-change-plan">
                    Change plan
                </Link>
                {data.subscription_status !== "cancelled" && (
                    <button onClick={cancel} data-testid="subscription-cancel" className="px-4 py-2 rounded-md border border-navy-500/20 text-navy-500/80 text-sm font-semibold hover:border-red-500/50 hover:text-red-600 inline-flex items-center gap-1.5">
                        <X className="w-4 h-4" /> Cancel auto-renew
                    </button>
                )}
            </div>
        </div>
    );
}
